/**
 * The spec store — a list of equipment kept in the browser.
 *
 * TWO of them exist, and keeping them apart is the whole point (owner, 2026-08-06):
 *
 *   • `window.BQSpec`  — «Your spec», the FINAL list. A product page, a listing card and the
 *     builder's «Add to spec» write here; `/cart` shows it and the quote is sent from it. Key
 *     `bq_builder_v1` — the key the storefront has been writing since the spec shipped, so nobody
 *     loses the list he already assembled.
 *   • `window.BQSetup` — the builder's working list, created by the builder page itself. Somebody
 *     trying our cocktail-bar setup on for size must not touch, and must not wipe, the list he has
 *     already decided on.
 *
 * Why a list at all rather than a Shopify cart: almost nothing we sell is bought off the shelf, the
 * sale runs through a quote, and a cart cannot hold a line that must not reach checkout — checkout
 * takes the cart whole. Direct purchase survives as «Buy now» on a CJ product, which clears the
 * cart, adds that variant and goes to /checkout without ever showing a cart page — and as /cart's
 * «Checkout» when every line is a CJ product in stock (`cj` / `checkout_id`, set by hydrate()).
 *
 * Saved copies live in our own API (`POST /specs` → a 12-char code, `PUT /specs/{code}` with the
 * edit token this browser keeps). A forwarded link carries the code alone, which is read-only. Each
 * store owns its own code: the builder's link reopens a setup in the builder, the spec's reopens
 * the spec in the cart.
 *
 * Loaded site-wide from snippets/spec-store.liquid. Anything that draws a list uses
 * assets/spec-table.js on top of a store.
 */
(function () {
  // Storefront root of the locale being browsed — `/` or `/de/`, always trailing-slashed.
  // A bare '/products/…' fetch answers from the primary locale, so a visitor in /de would get
  // English titles and the default market's prices.
  var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cfgEl = document.querySelector('[data-spec-config]');
  var stringsEl = document.querySelector('[data-spec-strings]');

  var cfg = {
    moneyFormat: (cfgEl && cfgEl.dataset.moneyFormat) || '${{amount}}',
    currency: (cfgEl && cfgEl.dataset.currency) || 'USD',
    leadsUrl: (cfgEl && cfgEl.dataset.leadsUrl) || '',
    specUrl: (cfgEl && cfgEl.dataset.specUrl) || '/cart',
  };

  /**
   * Strings ride on data attributes: an asset cannot call `| t`, and this theme allows no inline
   * <script>. Same contract as assets/lead-submit.js.
   */
  function t(key, fallback) {
    if (!stringsEl) return fallback;
    var value = stringsEl.getAttribute('data-' + key.replace(/_/g, '-'));
    return value === null || value === '' ? fallback : value;
  }

  /**
   * One store: its own storage key, its own event, and — for the spec — the header counter.
   *
   * @param {{key: string, event: string, badge?: string}} options
   */
  function createStore(options) {
    var KEY = options.key;
    var EVENT = options.event;

    /* ------------------------------------------------------------------- state */

    // { setup, preset, rows, items, dirty, owned } — `setup`/`preset`/`rows` are the builder's (the
    // venue format it started from and the checklist of categories to close) and sit unused in the
    // spec's own store; `items` is the list itself. `owned` = the saved copy THIS browser may rewrite.
    //
    // The object is never REPLACED, only mutated: the builder holds a reference to it.
    var state = { setup: null, preset: 'must', rows: [], items: {}, dirty: false, owned: null };

    function readStored() {
      try {
        var raw = localStorage.getItem(KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch (err) {
        // Private mode, disabled storage, corrupt value — the page still works for this visit.
        return null;
      }
    }

    // Kept as it was at boot, because the builder branches on it: a `#setup=` or `#s=` in the URL
    // outranks whatever this browser had lying around.
    var stored = readStored();
    if (stored) {
      state.setup = stored.setup || null;
      state.preset = stored.preset || 'must';
      state.rows = stored.rows || [];
      state.items = stored.items || {};
      state.dirty = !!stored.dirty;
      state.owned = stored.owned || null;
    }

    function persist() {
      try {
        localStorage.setItem(KEY, JSON.stringify(state));
      } catch (err) {
        /* nothing to do — the visit continues in memory */
      }
    }

    function emit() {
      document.dispatchEvent(new CustomEvent(EVENT, {
        detail: { count: count(), totals: totals() },
      }));
    }

    /** Write and tell the page. Anything that changes the list ends here. */
    function save() {
      persist();
      emit();
    }

    /* --------------------------------------------------------------- utilities */

    /**
     * Cents in the shop's own money format, so a total here reads exactly like a price on a product
     * card. This store uses `$ {{amount_no_decimals_with_space_separator}}`, so the space variants
     * are not theoretical — miss one and the placeholder prints raw.
     */
    function money(cents) {
      var parts = ((cents || 0) / 100).toFixed(2).split('.');

      function group(separator) {
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      }

      var values = {
        amount: group(',') + '.' + parts[1],
        amount_no_decimals: group(','),
        amount_with_comma_separator: group('.') + ',' + parts[1],
        amount_no_decimals_with_comma_separator: group('.'),
        amount_with_space_separator: group(' ') + ',' + parts[1],
        amount_no_decimals_with_space_separator: group(' '),
        amount_with_apostrophe_separator: group("'") + '.' + parts[1],
        amount_with_period_and_space_separator: group(' ') + '.' + parts[1],
      };

      return cfg.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
        return Object.prototype.hasOwnProperty.call(values, token) ? values[token] : match;
      });
    }

    function titleize(slug) {
      return String(slug || '').replace(/-/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); });
    }

    /**
     * A spec line is a PRODUCT — or one of its variants, once someone picks one. The key says which:
     * `handle`, or `handle::<variantId>`. Two sizes of the same kegerator are two lines; «the
     * kegerator, size unspecified» — which is all a preset can give us — is one.
     */
    /** A variant id as this list stores it — a string, or nothing at all. */
    function variantKey(value) {
      if (value === null || value === undefined || value === '') return null;
      return String(value);
    }

    function keyOf(handle, variantId) {
      return variantId ? handle + '::' + variantId : handle;
    }

    function count() {
      return Object.keys(state.items).length;
    }

    /**
     * What the list adds up to. `from` is true while any line is unpriced or made-to-order: a total
     * that silently omits a line, or quotes a configuration nobody asked for, reads as final when it
     * is not.
     */
    function totals() {
      var keys = Object.keys(state.items);
      var subtotal = 0;
      var priced = 0;
      var starting = false;

      keys.forEach(function (key) {
        var line = state.items[key];
        if (line.customizable) starting = true;
        if (typeof line.price === 'number') {
          subtotal += line.price * (line.qty || 1);
          priced += 1;
        }
      });

      return {
        count: keys.length,
        subtotal: subtotal,
        from: priced < keys.length || starting,
        currency: cfg.currency,
      };
    }

    /* ------------------------------------------------------------- mutations */

    /** Mark the list as hand-edited. Only applying a builder preset clears this. */
    function touch() {
      state.dirty = true;
    }

    /**
     * Add a position, or raise the one already there.
     *
     * Picking a size for a product that sits in the list «unspecified» REPLACES that line rather
     * than adding a second one — it is the same position, now decided, and it keeps the quantity and
     * the customization brief already written for it.
     */
    function add(line) {
      var handle = line.handle;
      if (!handle) return null;

      // ALWAYS a string. A variant id reaches us as a number from anything that read it out of
      // Shopify's own JSON — the product page's `variant.id`, a setup composition — and as a string
      // from anything that read it off a data attribute. The API takes a string and rejects the
      // whole spec otherwise (`422 body.items.N.variant_id: string_type`), which is how saving
      // broke: the visitor lost the link, not the line.
      var variantId = variantKey(line.variant_id);
      var key = keyOf(handle, variantId);
      var moq = line.moq || 1;
      var bare = variantId ? state.items[keyOf(handle, null)] : null;
      if (bare) delete state.items[keyOf(handle, null)];

      var existing = state.items[key];
      if (existing) {
        // Adding the same thing twice means more of it, not a duplicate line.
        existing.qty = Math.max(moq, (existing.qty || moq) + (line.qty || moq));
        save();
        return key;
      }

      // The line it replaces was already at a quantity somebody chose — deciding its size must not
      // quietly take it back down to the minimum.
      var wanted = line.qty || moq;
      if (bare && bare.qty) wanted = Math.max(wanted, bare.qty);

      state.items[key] = {
        handle: handle,
        variant_id: variantId,
        variant_title: line.variant_title || null,
        title: line.title || null,
        // A missing data attribute reaches us as NaN, and NaN in one line makes every total on the
        // site read NaN. Unknown means null — the line then says «—» and hydrate fills it in.
        price: typeof line.price === 'number' && isFinite(line.price) ? line.price : null,
        image: line.image || null,
        url: line.url || null,
        category: line.category || (bare ? bare.category : null) || null,
        priority: bare ? bare.priority : (line.priority || null),
        qty: Math.max(moq, wanted),
        moq: moq,
        customizable: !!line.customizable,
        // A brief written elsewhere travels with the position — the builder hands one over when
        // its setup is added to the spec.
        note: (bare && bare.note) || line.note || null,
        from_preset: bare ? bare.from_preset : !!line.from_preset,
      };
      touch();
      save();
      return key;
    }

    function remove(key) {
      if (!state.items[key]) return;
      delete state.items[key];
      touch();
      save();
    }

    function setQty(key, next) {
      var line = state.items[key];
      if (!line) return;
      var floor = line.moq || 1;
      // Below MOQ is a quantity we cannot ship, so the floor holds rather than walking through it.
      // Dropping the position entirely is the × next to it.
      line.qty = Math.max(floor, next);
      touch();
      save();
    }

    function bump(key, delta) {
      var line = state.items[key];
      if (line) setQty(key, (line.qty || 1) + delta);
    }

    /**
     * Swap the size of a position for another one, in place (owner, 2026-08-06). Picking the wrong
     * variant is the mistake this list invites most, and the answer used to be «take it out and go
     * find the product again» — three screens away from the list being read.
     *
     * The key of a line IS its variant (`handle::id`), so this is a rename: the line keeps its
     * quantity, its brief and its place in the list, and takes the new size's price. If the target
     * size is already in the list the two merge on the larger quantity rather than one silently
     * eating the other.
     */
    function setVariant(key, variant) {
      var line = state.items[key];
      if (!line || !variant) return key;

      var nextKey = keyOf(line.handle, variant.id);
      if (nextKey === key) return key;

      var existing = state.items[nextKey];
      var moved = {};
      Object.keys(line).forEach(function (field) { moved[field] = line[field]; });
      moved.variant_id = variantKey(variant.id);
      moved.variant_title = variant.title || null;
      if (typeof variant.price === 'number' && isFinite(variant.price)) moved.price = variant.price;
      // The size bought at checkout is the size on the line — never the one it had before.
      moved.checkout_id = variant.available ? String(variant.id) : null;
      if (existing) {
        moved.qty = Math.max(existing.qty || 1, line.qty || 1);
        moved.note = existing.note || line.note || null;
      }

      // Rebuilt in order rather than deleted-and-appended: an object's keys keep insertion order,
      // and a line that jumped to the bottom of the list on a size change would read as a new one.
      var next = {};
      Object.keys(state.items).forEach(function (field) {
        if (field === key) next[nextKey] = moved;
        else if (field !== nextKey) next[field] = state.items[field];
      });
      state.items = next;

      touch();
      save();
      return nextKey;
    }

    /**
     * The customization brief. Written on every keystroke, and deliberately WITHOUT an event: the
     * list is redrawn whole on `spec:updated`, and a redraw per keystroke pulls the field out from
     * under the cursor.
     */
    function setNote(key, value) {
      var line = state.items[key];
      if (!line) return;
      line.note = value;
      touch();
      persist();
    }

    /**
     * Learn the real supplier minimum for a line that was restored from a link or came from a
     * builder preset — neither can know it — and lift a quantity sitting under it.
     */
    function setMoq(key, moq) {
      var line = state.items[key];
      if (!line || !moq || line.moq === moq) return false;
      line.moq = moq;
      if (line.qty < moq) {
        line.qty = moq;
        save();
        return true;
      }
      persist();
      return false;
    }

    /* ----------------------------------------------------------- prices, live */

    /**
     * Fill in title, image and price for lines we only know by handle — builder preset lines and
     * anything restored from a saved link — so an old link never quotes yesterday's price back.
     *
     * `force` re-reads lines that already carry a price: that is what /cart wants, since it is where
     * the visitor reads the total before asking us to quote it.
     */
    function hydrate(keys, options) {
      var opts = options || {};
      var queue = (keys || Object.keys(state.items)).filter(function (key) {
        var line = state.items[key];
        if (!line) return false;
        return opts.force || line.price === null || line.price === undefined;
      });
      var inFlight = 0;
      var MAX = 4;

      return new Promise(function (resolve) {
        function pump() {
          if (!queue.length && inFlight === 0) {
            resolve();
            return;
          }
          while (inFlight < MAX && queue.length) {
            (function (key) {
              var line = state.items[key];
              inFlight += 1;
              fetch(localeRoot + 'products/' + encodeURIComponent(line.handle) + '.js')
                .then(function (res) { return res.ok ? res.json() : null; })
                .then(function (product) {
                  if (product && line) {
                    line.title = product.title;
                    line.image = product.featured_image || line.image || null;
                    line.url = product.url;
                    line.missing = false;
                    // Made-to-order, read from the product's own tags — the same gate the builder's
                    // rows use. A preset line and a restored line never went through one, so this is
                    // the only place they can learn that their price is a starting point.
                    line.customizable = (product.tags || []).indexOf('flag:customizable') !== -1;
                    var variant = null;
                    if (line.variant_id) {
                      (product.variants || []).forEach(function (v) {
                        if (String(v.id) === String(line.variant_id)) variant = v;
                      });
                    }
                    line.price = variant ? variant.price : product.price;
                    if (variant) line.variant_title = variant.title;
                    // What /cart needs to decide between checkout and a quote (owner, 2026-09-28):
                    // a CJ product (`supplier:CJ`, 1:1 with shipping.class dropship_free) can be
                    // bought outright, and only a variant in stock can reach checkout. A line with
                    // no size picked is buyable only when there is no size to pick.
                    line.cj = (product.tags || []).indexOf('supplier:CJ') !== -1;
                    var buyable = variant ||
                      (!line.variant_id && (product.variants || []).length === 1 ? product.variants[0] : null);
                    line.checkout_id = buyable && buyable.available ? String(buyable.id) : null;
                  } else if (line) {
                    // Retired or unpublished since the list was built. Kept and flagged — dropping a
                    // line silently would leave the visitor quoting a spec he never saw.
                    line.missing = true;
                    line.checkout_id = null;
                  }
                })
                .catch(function () {})
                .then(function () {
                  inFlight -= 1;
                  if (typeof opts.onTick === 'function') opts.onTick();
                  else emit();
                  pump();
                });
            })(queue.shift());
          }
        }
        pump();
      });
    }

    /* ------------------------------------------------------------- saved copy */

    /**
     * `POST /specs`. Derived from the leads endpoint the theme already carries
     * (`settings.leads_api_url`), so there is one API base to configure, not two.
     */
    function specsUrl() {
      return (cfg.leadsUrl || '').replace(/\/leads\/?$/, '/specs');
    }

    /**
     * The spec as it goes to the API, plus the state line behind each position so a caller can reach
     * the title and price without looking anything up.
     *
     * ⚠️ `handle` is `line.handle`, NOT the state key. The key of a line that names a variant is
     * `handle::<variantId>` — saved as the handle, it produced `/products/beer-kegerator::4368….js`
     * on restore, a 404, and the position came back «no longer available».
     */
    function specLines() {
      return Object.keys(state.items).map(function (key) {
        var line = state.items[key];
        return {
          key: key,
          line: line,
          item: {
            handle: line.handle,
            qty: line.qty || 1,
            category: line.category || null,
            priority: line.priority || null,
            // Normalized on the way OUT as well as on the way in: a browser that stored a numeric
            // id before this was fixed still holds one, and it must not keep failing every save.
            variant_id: variantKey(line.variant_id),
            from_preset: !!line.from_preset,
            // Travels with the position: nothing on the storefront can hand a MOQ back to a spec
            // that only knows handles.
            moq: line.moq || 1,
            note: (line.note || '').trim() || null,
          },
        };
      });
    }

    function specPayload() {
      var lines = specLines();
      var total = 0;
      lines.forEach(function (entry) {
        if (typeof entry.line.price === 'number') total += entry.line.price * entry.item.qty;
      });
      return {
        items: lines.map(function (entry) { return entry.item; }),
        setup_slug: state.setup || null,
        preset: state.setup ? state.preset : null,
        totals: { count: lines.length, subtotal: total / 100, currency: cfg.currency },
      };
    }

    /** A new row, and with it the write capability for it. */
    function createSpec() {
      var url = specsUrl();
      if (!url) return Promise.resolve(null);
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(specPayload()),
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data || !data.code) return null;
          // The token never leaves this browser. A forwarded link carries the code alone, which is
          // read-only — someone you sent your spec to cannot rewrite it under you.
          state.owned = { code: data.code, token: data.edit_token || null };
          persist();
          return data.code;
        })
        .catch(function () { return null; });
    }

    /**
     * Returns the code, or null when saving failed — the caller decides whether that is fatal.
     *
     * Saving again REWRITES the spec this browser saved rather than minting a second code: the first
     * link is the one already pasted into a chat.
     */
    function saveSpec() {
      var url = specsUrl();
      if (!url) return Promise.resolve(null);

      var owned = state.owned;
      if (!owned || !owned.code || !owned.token) return createSpec();

      var payload = specPayload();
      payload.edit_token = owned.token;
      return fetch(url + '/' + encodeURIComponent(owned.code), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (res.ok) return owned.code;
          // The row is gone, or an older save has no token to match. A new code costs the visitor a
          // stale link; failing outright would cost him the spec.
          if (res.status === 404) {
            state.owned = null;
            persist();
            return createSpec();
          }
          return null;
        })
        .catch(function () { return null; });
    }

    function fetchSpec(code) {
      var url = specsUrl();
      if (!url) return Promise.resolve(null);
      return fetch(url + '/' + encodeURIComponent(code))
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    }

    /**
     * A saved spec, opened. It wins over whatever this browser had: someone is looking at a list
     * that was sent to him, and showing his own last session instead would be the wrong list.
     *
     * `rowMeta(leaf)` lets the builder attach zone titles from the taxonomy it has parsed; anywhere
     * else the rows are just handles, which is all /cart needs.
     */
    function applySpec(spec, rowMeta) {
      state.setup = spec.setup_slug || null;
      state.preset = spec.preset || state.preset;
      state.items = {};

      var rows = [];
      var seen = {};
      (spec.items || []).forEach(function (item) {
        state.items[keyOf(item.handle, variantKey(item.variant_id))] = {
          handle: item.handle,
          category: item.category || null,
          priority: item.priority || null,
          qty: item.qty || 1,
          // The floor travels WITH the spec — unlike the price, which is re-read live. A MOQ cannot
          // be re-read from a handle at all, so a link opened on another device would otherwise let
          // the visitor take a hundred-piece minimum down to one.
          moq: item.moq || 1,
          from_preset: !!item.from_preset,
          variant_id: variantKey(item.variant_id),
          note: item.note || null,
          title: null,
          price: null,
        };
        if (item.category && !seen[item.category]) {
          seen[item.category] = true;
          var meta = typeof rowMeta === 'function' ? (rowMeta(item.category) || {}) : {};
          rows.push({ leaf: item.category, zone: meta.zone || null, zoneTitle: meta.zoneTitle || null });
        }
      });

      state.rows = rows;
      state.dirty = true;
      // Someone else's spec: we may read it, not rewrite it. Saving from here mints a copy of our
      // own, which is what the sender expects — his link keeps showing what he sent.
      state.owned = null;
      save();
    }

    /** The link that reopens this spec, on the page it was saved from (owner, 2026-08-05). */
    function specLink(code) {
      return location.origin + location.pathname + '#s=' + code;
    }

    /**
     * Copy without losing the click. `navigator.clipboard.writeText` called AFTER an awaited fetch
     * has no transient user activation left in Safari and Firefox: the promise rejects, and a button
     * that says «Link copied» has copied nothing. Safari accepts a ClipboardItem whose value is a
     * PROMISE — the write starts inside the click and settles when the save comes back.
     *
     * Resolves true only if the clipboard actually took it, so the message can tell the truth.
     */
    function copyWhileActive(textPromise) {
      if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        try {
          var blob = textPromise.then(function (text) {
            return new Blob([text], { type: 'text/plain' });
          });
          return navigator.clipboard
            .write([new ClipboardItem({ 'text/plain': blob })])
            .then(function () { return true; }, function () { return false; });
        } catch (e) {
          // An older ClipboardItem that wants a Blob, not a promise for one — fall through.
        }
      }
      return textPromise.then(function (text) {
        if (!navigator.clipboard) return false;
        return navigator.clipboard.writeText(text)
          .then(function () { return true; }, function () { return false; });
      }, function () { return false; });
    }

    /* ----------------------------------------------------------------- toast */

    var toastTimer = null;

    /**
     * «Added to your spec», with the way to the list. A toast rather than a redirect: adding is not
     * leaving — the visitor is usually mid-page and about to add the next thing.
     */
    function toast(message) {
      var node = document.querySelector('[data-spec-toast]');
      if (!node) return;
      var text = node.querySelector('[data-spec-toast-text]');
      if (text) text.textContent = message || t('added', 'Added to your spec');
      node.hidden = false;
      // Reflow between the two so re-adding restarts the animation instead of leaving it finished.
      node.removeAttribute('data-shown');
      void node.offsetWidth;
      node.setAttribute('data-shown', '');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        node.removeAttribute('data-shown');
        toastTimer = setTimeout(function () { node.hidden = true; }, 300);
      }, 4000);
    }

    /* ------------------------------------------------------------------ badge */

    /**
     * The header counter. Only the SPEC paints it — the builder's working list is not what the
     * visitor is carrying. Written by the browser and never by Liquid: pages are cached at the edge,
     * and a personal list must not ride in a cached response.
     */
    function paintBadges() {
      if (!options.badge) return;
      var n = count();
      Array.prototype.forEach.call(document.querySelectorAll(options.badge), function (badge) {
        badge.textContent = String(n);
        if (n > 0) badge.removeAttribute('hidden');
        else badge.setAttribute('hidden', '');
      });
    }

    if (options.badge) {
      document.addEventListener(EVENT, paintBadges);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', paintBadges);
      } else {
        paintBadges();
      }
    }

    // Another tab of the same shop is the same list: adding there and coming back here must not show
    // a stale one.
    window.addEventListener('storage', function (event) {
      if (event.key !== KEY) return;
      var next = readStored();
      if (!next) return;
      state.setup = next.setup || null;
      state.preset = next.preset || 'must';
      state.rows = next.rows || [];
      state.items = next.items || {};
      state.dirty = !!next.dirty;
      state.owned = next.owned || null;
      emit();
    });

    return {
      key: KEY,
      event: EVENT,
      state: state,
      stored: stored,
      config: cfg,
      t: t,

      keyOf: keyOf,
      items: function () { return state.items; },
      line: function (key) { return state.items[key]; },
      count: count,
      totals: totals,
      money: money,
      titleize: titleize,

      add: add,
      remove: remove,
      setQty: setQty,
      bump: bump,
      setNote: setNote,
      setMoq: setMoq,
      setVariant: setVariant,
      touch: touch,
      save: save,
      persist: persist,
      emit: emit,

      hydrate: hydrate,
      specsUrl: specsUrl,
      specLines: specLines,
      specPayload: specPayload,
      saveSpec: saveSpec,
      fetchSpec: fetchSpec,
      applySpec: applySpec,
      specLink: specLink,
      copyWhileActive: copyWhileActive,

      toast: toast,
    };
  }

  window.BQSpecStore = { create: createStore, t: t, config: cfg };

  // The spec itself — the list the storefront sells from — is created here, because every page
  // has it: the header badge, «Add to spec» on a product page, /cart. The builder creates its own.
  window.BQSpec = createStore({
    key: 'bq_builder_v1',
    event: 'spec:updated',
    badge: '[data-spec-count]',
  });
})();
